// Rentabilidad por barrio.
// Suma ingresos por suscripciones (pagos) + pedidos one-shot agrupados por barrio del cliente.
// Cuenta entregas y pone una intensidad de zona.

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import AnalisisIA from '../../components/AnalisisIA'
import { supabase } from '../../lib/supabase'

const fmtUYU = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-UY')}`

export default function RentabilidadBarrio() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [meses, setMeses] = useState(3)
  const [filas, setFilas] = useState([])

  useEffect(() => { cargar() }, [meses])

  async function cargar() {
    setLoading(true)
    setError(null)
    try {
      const desde = new Date()
      desde.setMonth(desde.getMonth() - meses)
      desde.setDate(1)
      const desdeStr = desde.toISOString().slice(0, 10)

      // 1) Pagos de suscripciones — necesita unir con clientes para barrio
      const [{ data: pagos, error: pe }, { data: pedidos, error: pde }, { data: entregas, error: ee }] = await Promise.all([
        supabase
          .from('pagos')
          .select(`
            id, monto, pagado_at, cliente_id,
            cliente:clientes!pagos_cliente_id_fkey(barrio, barrio_id, barrio_obj:barrios!clientes_barrio_id_fkey(nombre))
          `)
          .eq('estado', 'pagado')
          .gte('pagado_at', desdeStr),
        supabase
          .from('pedidos')
          .select(`
            id, monto_total, created_at,
            barrio_obj:barrios!pedidos_entrega_barrio_id_fkey(nombre)
          `)
          .in('estado', ['pagado', 'en_armado', 'en_ruta', 'entregado'])
          .gte('created_at', desdeStr),
        supabase
          .from('entregas')
          .select(`
            id, fecha_programada, estado,
            cliente:clientes!entregas_cliente_id_fkey(barrio, barrio_id, barrio_obj:barrios!clientes_barrio_id_fkey(nombre))
          `)
          .gte('fecha_programada', desdeStr),
      ])
      if (pe) throw pe
      if (pde) throw pde
      if (ee) throw ee

      const agg = new Map()
      function ensure(barrio) {
        const b = (barrio || '— Sin barrio —').trim()
        if (!agg.has(b)) agg.set(b, {
          barrio: b,
          ingresos_susc: 0, n_pagos: 0,
          ingresos_oneshot: 0, n_pedidos: 0,
          entregas_total: 0, entregas_entregadas: 0,
        })
        return agg.get(b)
      }

      ;(pagos || []).forEach(p => {
        const b = p.cliente?.barrio_obj?.nombre || p.cliente?.barrio || '— Sin barrio —'
        const r = ensure(b)
        r.ingresos_susc += Number(p.monto || 0)
        r.n_pagos += 1
      })

      ;(pedidos || []).forEach(p => {
        const r = ensure(p.barrio_obj?.nombre)
        r.ingresos_oneshot += Number(p.monto_total || 0)
        r.n_pedidos += 1
      })

      ;(entregas || []).forEach(e => {
        const b = e.cliente?.barrio_obj?.nombre || e.cliente?.barrio || '— Sin barrio —'
        const r = ensure(b)
        r.entregas_total += 1
        if (e.estado === 'entregada' || e.estado === 'entregado') r.entregas_entregadas += 1
      })

      const arr = Array.from(agg.values()).map(r => ({
        ...r,
        ingresos_total: r.ingresos_susc + r.ingresos_oneshot,
      }))
      arr.sort((a, b) => b.ingresos_total - a.ingresos_total)
      setFilas(arr)
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  const totales = useMemo(() => {
    const t = { ingresos_susc: 0, ingresos_oneshot: 0, ingresos_total: 0, entregas_total: 0, entregas_entregadas: 0, n_pagos: 0, n_pedidos: 0 }
    filas.forEach(r => {
      t.ingresos_susc += r.ingresos_susc
      t.ingresos_oneshot += r.ingresos_oneshot
      t.ingresos_total += r.ingresos_total
      t.entregas_total += r.entregas_total
      t.entregas_entregadas += r.entregas_entregadas
      t.n_pagos += r.n_pagos
      t.n_pedidos += r.n_pedidos
    })
    return t
  }, [filas])

  const maxIng = filas[0]?.ingresos_total || 1

  return (
    <AdminLayout>
      <div className="page">
        <h1>🗺️ Rentabilidad por barrio</h1>
        <p className="muted">Ingresos y operación por zona. Mostrando los últimos {meses} meses.</p>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
            Período:
            <select value={meses} onChange={e => setMeses(Number(e.target.value))}
              style={{ marginLeft: 8, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}>
              <option value={1}>Último mes</option>
              <option value={3}>Últimos 3 meses</option>
              <option value={6}>Últimos 6 meses</option>
              <option value={12}>Último año</option>
            </select>
          </label>
        </div>

        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>Barrios activos</h3>
            <div className="stat">{filas.length}</div>
          </div>
          <div className="card">
            <h3>Ingresos totales</h3>
            <div className="stat" style={{ color: '#10B981' }}>{fmtUYU(totales.ingresos_total)}</div>
          </div>
          <div className="card">
            <h3>Entregas</h3>
            <div className="stat" style={{ color: '#3b82f6' }}>{totales.entregas_entregadas} / {totales.entregas_total}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>completadas / programadas</div>
          </div>
          <div className="card">
            <h3>Tickets</h3>
            <div className="stat">{totales.n_pagos + totales.n_pedidos}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{totales.n_pagos} susc · {totales.n_pedidos} one-shot</div>
          </div>
        </div>

        {error && <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', marginBottom: 12 }}>{error}</div>}
        {loading && <div className="ap-loading">Cargando…</div>}

        {!loading && filas.length === 0 && (
          <div className="ap-empty">
            <div style={{ fontSize: 48 }}>🌎</div>
            <h3>Sin datos en el período seleccionado</h3>
          </div>
        )}

        {!loading && filas.length > 0 && (
          <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <table className="usu-table">
              <thead>
                <tr>
                  <th>Barrio</th>
                  <th style={{ textAlign: 'right' }}>Ing. Suscripciones</th>
                  <th style={{ textAlign: 'right' }}>Ing. One-shot</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'center' }}>Entregas</th>
                  <th>Volumen</th>
                </tr>
              </thead>
              <tbody>
                {filas.map(r => {
                  const pct = maxIng > 0 ? (r.ingresos_total / maxIng) * 100 : 0
                  return (
                    <tr key={r.barrio}>
                      <td>
                        <div style={{ fontWeight: 700, color: '#1a3461' }}>{r.barrio}</div>
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {fmtUYU(r.ingresos_susc)}
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>{r.n_pagos} cobros</div>
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {fmtUYU(r.ingresos_oneshot)}
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>{r.n_pedidos} pedidos</div>
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#1a3461' }}>{fmtUYU(r.ingresos_total)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: 13 }}>{r.entregas_entregadas}</span>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}> / {r.entregas_total}</span>
                      </td>
                      <td style={{ minWidth: 200 }}>
                        <div style={{ background: '#f1f5f9', borderRadius: 6, height: 10, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, background: '#1a3461', height: '100%' }}></div>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <AnalisisIA
          reportType="rentabilidad_barrio"
          data={{ totales, barrios: filas.slice(0, 20), periodo_meses: meses }}
        />

        <div style={{ marginTop: 20, padding: 14, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 12, color: '#78350f' }}>
          <strong>📌 Notas:</strong> Los ingresos one-shot toman el barrio del pedido (puede diferir del catálogo).
          No se descuenta costo de envío ni mercadería en este reporte.
        </div>
      </div>

      <style>{`
        .usu-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .usu-table thead th {
          text-align: left; padding: 12px 14px;
          background: #f8fafc; border-bottom: 1px solid #e5e7eb;
          font-size: 12px; text-transform: uppercase; color: #6b7280;
          font-weight: 700; letter-spacing: .03em;
        }
        .usu-table tbody td {
          padding: 12px 14px; border-bottom: 1px solid #f1f5f9; vertical-align: top;
        }
        .usu-table tbody tr:hover { background: #f8fafc; }
        .ap-loading { padding: 40px; text-align: center; color: #6b7280; }
        .ap-empty { padding: 60px 20px; text-align: center; color: #6b7280; }
        .ap-empty h3 { color: #1a3461; margin: 16px 0 8px; }
      `}</style>
    </AdminLayout>
  )
}
